<div class="page-container gap-8">
  <!-- Sticky: title + New Form only -->
  <div
    class="sticky top-0 z-20 -mx-4 -mt-4 bg-slate-100 px-4 pt-4 pb-4 sm:pt-4">
    <app-page-header
      title="Manage your forms"
      description="Create, publish, and track all your forms from one clean workspace.">
      <lib-signal-button
        iconSrc="assets/icons/plus.svg"
        (clicked)="createNewForm()">
        New Form
      </lib-signal-button>
    </app-page-header>
  </div>

  <!-- Stats -->
  <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
    <div class="forms-stat-card flex flex-col gap-1 p-4">
      <div class="flex items-center justify-between gap-2">
        <p class="text-sm font-medium text-neutral-500">Total Forms</p>
        <span
          class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sky-50 text-sky-600"
          aria-hidden="true">
          <app-svg-icon src="assets/icons/view-agenda-outline.svg" [size]="18" />
        </span>
      </div>
      <p class="text-2xl font-semibold tracking-tight text-neutral-950 tabular-nums">
        {{ totalForms() }}
      </p>
    </div>

    <div class="forms-stat-card flex flex-col gap-1 p-4">
      <div class="flex items-center justify-between gap-2">
        <p class="text-sm font-medium text-neutral-500">Published</p>
        <span
          class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600"
          aria-hidden="true">
          <app-svg-icon src="assets/icons/check-circle-outline.svg" [size]="18" />
        </span>
      </div>
      <p class="text-2xl font-semibold tracking-tight text-neutral-950 tabular-nums">
        {{ publishedForms() }}
      </p>
    </div>

    <div class="forms-stat-card flex flex-col gap-1 p-4">
      <div class="flex items-center justify-between gap-2">
        <p class="text-sm font-medium text-neutral-500">Drafts</p>
        <span
          class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600"
          aria-hidden="true">
          <app-svg-icon src="assets/icons/square-edit-outline.svg" [size]="18" />
        </span>
      </div>
      <p class="text-2xl font-semibold tracking-tight text-neutral-950 tabular-nums">
        {{ draftForms() }}
      </p>
    </div>

    <div class="forms-stat-card flex flex-col gap-1 p-4">
      <div class="flex items-center justify-between gap-2">
        <p class="text-sm font-medium text-neutral-500">Unpublished</p>
        <span
          class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-50 text-violet-600"
          aria-hidden="true">
          <app-svg-icon src="assets/icons/eye-off-outline.svg" [size]="18" />
        </span>
      </div>
      <p class="text-2xl font-semibold tracking-tight text-neutral-950 tabular-nums">
        {{ unpublishedForms() }}
      </p>
    </div>
  </div>

  <!-- Toolbar -->
  <div class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
    <div class="w-full lg:max-w-md">
      <lib-signal-text-field
        label=""
        placeholder="Search forms..."
        appearance="fill"
        prefixIcon="assets/icons/search.svg"
        fillColor="#ffffff"
        [value]="searchText()"
        [debounceValueChange]="false"
        (valueChange)="onSearchValueChange($event)" />
    </div>

    <div class="flex flex-wrap gap-2">
      @for (filter of statusFilters; track filter.value) {
        <button
          type="button"
          (click)="setStatus(filter.value)"
          class="cursor-pointer rounded-full px-4 py-2 text-sm font-medium transition"
          [class.bg-primary-600]="selectedStatus() === filter.value"
          [class.text-white]="selectedStatus() === filter.value"
          [class.text-slate-500]="selectedStatus() !== filter.value"
          [class.hover:bg-white/80]="selectedStatus() !== filter.value">
          {{ filter.label }}
        </button>
      }
    </div>
  </div>

  <!-- Forms Grid -->
  <div class="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
    @for (form of filteredForms(); track form.id) {
      <div class="forms-form-card flex flex-col p-5 sm:p-6">
        <div class="flex items-center gap-3">
          <span
            class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-600"
            aria-hidden="true">
            <app-svg-icon src="assets/icons/file-document-outline.svg" [size]="22" />
          </span>

          <div class="min-w-0 flex-1">
            <div class="flex items-center justify-between gap-2">
              <h2 class="min-w-0 truncate text-base font-semibold text-neutral-950">
                {{ form.name }}
              </h2>
              <span
                class="inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium capitalize"
                [class]="getStatusClass(form.status)">
                <app-svg-icon [src]="getStatusIcon(form.status)" [size]="14" />
                {{ form.status }}
              </span>
            </div>
            @if (form.description) {
              <p class="mt-1 line-clamp-2 text-sm leading-5 text-neutral-500">
                {{ form.description }}
              </p>
            }
          </div>
        </div>

        <div class="mt-5 grid grid-cols-2 gap-0 divide-x divide-slate-100 rounded-xl bg-slate-50/80 px-1 py-3">
          <div class="flex items-center gap-2.5 px-3">
            <span
              class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-50 text-violet-600"
              aria-hidden="true">
              <app-svg-icon src="assets/icons/chart-bar.svg" [size]="18" />
            </span>
            <div class="min-w-0">
              <p class="text-xs font-medium text-neutral-400">Submissions</p>
              <p class="mt-0.5 text-base font-semibold tabular-nums text-neutral-950">
                {{ getSubmissionCount(form) }}
              </p>
            </div>
          </div>
          <div class="flex items-center gap-2.5 px-3">
            <span
              class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500"
              aria-hidden="true">
              <app-svg-icon src="assets/icons/clock-outline.svg" [size]="18" />
            </span>
            <div class="min-w-0">
              <p class="text-xs font-medium text-neutral-400">Last updated</p>
              <p class="mt-0.5 truncate text-base font-semibold text-neutral-950">
                {{ form.updatedAt | date: 'mediumDate' }}
              </p>
            </div>
          </div>
        </div>

        <div
          class="mt-4 grid gap-2"
          [class.grid-cols-3]="form.status === publishedStatus"
          [class.grid-cols-2]="form.status !== publishedStatus">
          <button
            type="button"
            (click)="viewFormData(form)"
            class="inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-xl bg-primary-600 px-3 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-700">
            <app-svg-icon src="assets/icons/chart-bar.svg" [size]="16" />
            View data
          </button>
          @if (form.status === publishedStatus) {
            <button
              type="button"
              (click)="shareForm(form)"
              class="inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-neutral-700 transition hover:bg-slate-50">
              <app-svg-icon src="assets/icons/share-variant-outline.svg" [size]="16" />
              Share
            </button>
          }
          <button
            type="button"
            (click)="openForm(form)"
            class="inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-neutral-700 transition hover:bg-slate-50">
            <app-svg-icon src="assets/icons/square-edit-outline.svg" [size]="16" />
            Edit
          </button>
        </div>
      </div>
    } @empty {
      @if (listState.loading()) {
        <div class="col-span-full py-6 text-center text-sm text-neutral-500">
          Loading forms…
        </div>
      }
    }

    <!-- Create new form CTA -->
    <button
      type="button"
      class="forms-form-card flex min-h-[220px] cursor-pointer flex-col items-center justify-center gap-3 p-6 text-center transition hover:border-primary-200 hover:bg-primary-50/40"
      (click)="createNewForm()">
      <span
        class="flex h-12 w-12 items-center justify-center rounded-full bg-primary-50 text-primary-600"
        aria-hidden="true">
        <svg class="h-6 w-6" viewBox="0 0 24 24" fill="none">
          <path
            d="M12 5v14M5 12h14"
            stroke="currentColor"
            stroke-width="2.2"
            stroke-linecap="round" />
        </svg>
      </span>
      <div class="min-w-0">
        <h2 class="text-base font-semibold text-neutral-950">Create a new form</h2>
        <p class="mt-1 text-sm text-neutral-500">
          Start from scratch or use a template.
        </p>
      </div>
    </button>
  </div>
</div>
